import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, Shield, Activity, Users, Database, FileText, CreditCard, Key, Lock, Mail } from 'lucide-react';
import api from '../../lib/api';
import { toast } from 'sonner';

export default function SystemReset() {
  const [resetConfirmText, setResetConfirmText] = useState('');
  const [resetting, setResetting] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [adminPassword, setAdminPassword] = useState('');
  const [sending, setSending] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [passwordError, setPasswordError] = useState('');

  const handleRequestReset = async () => {
    if (resetConfirmText !== 'RESET ILEADX') {
      toast.error('You must type RESET ILEADX exactly to confirm');
      return;
    }
    setShowPasswordModal(true);
  };

  const handleSendResetLink = async () => {
    if (!adminPassword) {
      toast.error('Please enter your password');
      return;
    }
    setPasswordError('');
    setSending(true);
    try {
      await api.post('/api/admin/system/reset/request', { password: adminPassword });
      setEmailSent(true);
      setShowPasswordModal(false);
      setAdminPassword('');
      toast.success('Confirmation email sent! Check your inbox.');
    } catch (err) {
      if (err.response?.status === 401) {
        setPasswordError('Incorrect password');
      } else {
        setPasswordError(err.response?.data?.detail || 'Failed to send confirmation email');
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="page-content" style={{ maxWidth: '100%' }}>
      <div style={{
        background: 'var(--bg-secondary)',
        border: '2px solid var(--error)',
        borderRadius: 'var(--radius-xl)',
        padding: 'var(--space-2xl)',
        boxShadow: 'var(--shadow-md)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24 }}>
          <span style={{
            width: 56, height: 56, borderRadius: 'var(--radius-lg)',
            background: 'var(--error-bg)', display: 'flex',
            alignItems: 'center', justifyContent: 'center',
            fontSize: 28,
          }}>☢️</span>
          <div>
            <h3 style={{ color: 'var(--error)', fontSize: 20, fontWeight: 700, margin: 0 }}>
              Nuclear System Reset
            </h3>
            <p style={{ color: 'var(--text-secondary)', fontSize: 13, margin: '4px 0 0' }}>
              Two-step verification: password → email confirmation → reset
            </p>
          </div>
        </div>

        {/* Email Sent Success */}
        {emailSent ? (
          <div style={{
            padding: 'var(--space-2xl)', background: 'var(--success-bg)',
            borderRadius: 'var(--radius-lg)', border: '1px solid var(--success)',
            textAlign: 'center',
          }}>
            <Mail size={48} color="var(--success)" style={{ marginBottom: 16 }} />
            <h3 style={{ color: 'var(--success)', fontSize: 18, fontWeight: 700, margin: '0 0 8px' }}>
              Confirmation Email Sent
            </h3>
            <p style={{ color: 'var(--text-secondary)', fontSize: 14, lineHeight: 1.6, marginBottom: 8 }}>
              Check your inbox for the reset confirmation link. The link expires in 10 minutes.
            </p>
            <p style={{ color: 'var(--text-tertiary)', fontSize: 12 }}>
              Click the link in the email to complete the system reset.
            </p>
          </div>
        ) : (
          <>
            <div style={{
              padding: 'var(--space-xl)',
              background: 'var(--error-bg)',
              borderRadius: 'var(--radius-lg)',
              border: '1px solid var(--error)',
              marginBottom: 28,
            }}>
              <h4 style={{ color: 'var(--error)', fontSize: 14, fontWeight: 700, margin: '0 0 16px' }}>
                ⚠️ What This Destroys
              </h4>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 10 }}>
                {[
                  { icon: <Users size={14} />, text: 'All user accounts (except yours)' },
                  { icon: <Database size={14} />, text: 'All leads and search history' },
                  { icon: <Activity size={14} />, text: 'All AI scores and pitch angles' },
                  { icon: <CreditCard size={14} />, text: 'All payment records' },
                  { icon: <Key size={14} />, text: 'All integration connections' },
                  { icon: <FileText size={14} />, text: 'All audit logs and admin notes' },
                  { icon: <Shield size={14} />, text: 'All background jobs and queues' },
                  { icon: <FileText size={14} />, text: 'All export history' },
                ].map((item, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--error)', fontSize: 13 }}>
                    {item.icon} {item.text}
                  </div>
                ))}
              </div>
            </div>

            <div style={{ marginBottom: 24 }}>
              <label style={{
                display: 'block', color: 'var(--text-secondary)', fontSize: 12,
                fontWeight: 600, marginBottom: 8, textAlign: 'center',
              }}>
                Step 1: Type <strong style={{ color: 'var(--error)' }}>RESET ILEADX</strong> to begin:
              </label>
              <input
                value={resetConfirmText}
                onChange={(e) => setResetConfirmText(e.target.value)}
                placeholder="RESET ILEADX"
                style={{
                  width: '100%', padding: '14px 16px', borderRadius: 10,
                  border: '2px solid var(--error)', background: 'var(--input-bg)',
                  color: 'var(--text-primary)', fontSize: 18, outline: 'none',
                  textAlign: 'center', letterSpacing: 3, fontWeight: 700,
                  fontFamily: 'monospace', boxSizing: 'border-box',
                }}
              />
            </div>

            <button
              onClick={handleRequestReset}
              disabled={resetConfirmText !== 'RESET ILEADX'}
              style={{
                width: '100%', padding: '16px', borderRadius: 12, border: 'none',
                background: resetConfirmText === 'RESET ILEADX' ? 'var(--error)' : 'var(--bg-tertiary)',
                color: resetConfirmText === 'RESET ILEADX' ? 'white' : 'var(--text-tertiary)',
                cursor: resetConfirmText === 'RESET ILEADX' ? 'pointer' : 'not-allowed',
                fontSize: 16, fontWeight: 700,
                transition: 'all 0.2s ease',
              }}
            >
              Continue to Password Verification
            </button>
          </>
        )}
      </div>

      {/* Password Confirmation Modal */}
      {showPasswordModal && createPortal(
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 99999, padding: 20,
        }} onClick={() => { setShowPasswordModal(false); setAdminPassword(''); setPasswordError(''); }}>
          <div style={{
            background: 'var(--bg-secondary)', borderRadius: 20, padding: '32px 36px',
            maxWidth: 440, width: '100%', border: '1px solid var(--border-primary)',
            boxShadow: 'var(--shadow-xl)', textAlign: 'center',
          }} onClick={e => e.stopPropagation()}>
            {sending ? (
              <>
                <div style={{
                  width: 48, height: 48, borderRadius: '50%',
                  border: '3px solid var(--border-primary)',
                  borderTop: '3px solid var(--accent-primary)',
                  animation: 'spin 0.8s linear infinite',
                  margin: '0 auto 20px',
                }} />
                <h3 style={{ color: 'var(--text-primary)', fontSize: 16, fontWeight: 700, margin: '0 0 8px' }}>
                  Verifying password...
                </h3>
                <p style={{ color: 'var(--text-secondary)', fontSize: 13, margin: 0 }}>
                  Please wait
                </p>
              </>
            ) : (
              <>
                <Lock size={48} color="var(--error)" style={{ marginBottom: 16 }} />
                <h3 style={{ color: 'var(--text-primary)', fontSize: 18, fontWeight: 700, margin: '0 0 8px' }}>
                  Verify Your Identity
                </h3>
                <p style={{ color: 'var(--text-secondary)', fontSize: 14, margin: '0 0 20px', lineHeight: 1.6 }}>
                  Step 2: Enter your account password. A confirmation link will be sent to your email.
                </p>
      
                {passwordError && (
                  <div style={{
                    padding: '10px 14px', background: 'var(--error-bg)',
                    borderRadius: 8, border: '1px solid var(--error)',
                    color: 'var(--error)', fontSize: 13, marginBottom: 16,
                  }}>
                    {passwordError}
                  </div>
                )}
      
                <input
                  type="password"
                  value={adminPassword}
                  onChange={(e) => { setAdminPassword(e.target.value); setPasswordError(''); }}
                  placeholder="Enter your password"
                  autoFocus
                  style={{
                    width: '100%', padding: '14px 16px', borderRadius: 10,
                    border: passwordError ? '1px solid var(--error)' : '1px solid var(--border-primary)',
                    background: 'var(--input-bg)',
                    color: 'var(--text-primary)', fontSize: 15, outline: 'none',
                    marginBottom: 20, boxSizing: 'border-box',
                  }}
                />
                <div style={{ display: 'flex', gap: 12 }}>
                  <button onClick={() => { setShowPasswordModal(false); setAdminPassword(''); setPasswordError(''); }} style={{
                    flex: 1, padding: '14px', borderRadius: 12,
                    border: '1px solid var(--border-primary)', background: 'transparent',
                    color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 14, fontWeight: 600,
                  }}>Cancel</button>
                  <button onClick={handleSendResetLink} disabled={!adminPassword} style={{
                    flex: 1, padding: '14px', borderRadius: 12, border: 'none',
                    background: 'var(--error)', color: 'white',
                    cursor: 'pointer', fontSize: 14, fontWeight: 700,
                    opacity: !adminPassword ? 0.5 : 1,
                  }}>
                    Send Reset Link
                  </button>
                </div>
              </>
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}